import { Label } from '@/components/ui/label.tsx'
import { Input } from '@/components/ui/input.tsx'
import { useBranding } from '@/contexts/BrandingContext.tsx'
import SaveBotton from './SaveBotton'
import { BrandingSettings } from '@/types/branding'
import { useState, useEffect } from 'react'

function Basic({ customCopy, setCustomCopy }: { customCopy: BrandingSettings['customCopy'], setCustomCopy: (customCopy: BrandingSettings['customCopy']) => void }) {
  const { branding, updateBranding, isLoading } = useBranding()
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    setHasChanges(false) // Reset changes when component mounts
  }, [])

  const handleChange = (field: keyof BrandingSettings['customCopy'], value: string) => {
    setCustomCopy({ ...customCopy, [field]: value })
    setHasChanges(true) // Mark as changed
  }

  const handleSave = async () => {
    await updateBranding({ ...branding, customCopy })
    setHasChanges(false) // Reset changes after successful save
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label>Welcome Message</Label>
            <Input
              value={customCopy.welcomeMessage}
              onChange={(e) => handleChange('welcomeMessage', e.target.value)}
              placeholder="Welcome to our AI scheduling platform"
            />
            <p className="text-xs text-muted-foreground mt-1">
              General welcome message used across the platform
            </p>
          </div>

          <div>
            <Label>AI Assistant Name</Label>
            <Input
              value={customCopy.aiName}
              onChange={(e) => handleChange('aiName', e.target.value)}
              placeholder="Jade (default)"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Name of your AI assistant that customers will interact with
            </p>
          </div>

          <div>
            <Label>Call-to-Action Text</Label>
            <Input
              value={customCopy.ctaText}
              onChange={(e) => handleChange('ctaText', e.target.value)}
              placeholder="Start Your Free Trial"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Primary action button text used throughout the platform
            </p>
          </div>

          <div>
            <Label>Tagline</Label>
            <Input
              value={customCopy.tagline}
              onChange={(e) => handleChange('tagline', e.target.value)}
              placeholder="Intelligent scheduling made simple"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Short description of your service value proposition
            </p>
          </div>
        </div>

        <div>
          <h4 className="font-medium mb-3">Copy Guidelines</h4>
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="font-medium text-blue-900 mb-1">Keep it concise</p>
              <p className="text-blue-700">Use clear, simple language that reflects your brand voice</p>
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <p className="font-medium text-green-900 mb-1">Stay consistent</p>
              <p className="text-green-700">Maintain consistent terminology across all customer touchpoints</p>
            </div>
            <div className="p-3 bg-purple-50 border border-purple-200 rounded">
              <p className="font-medium text-purple-900 mb-1">Test variations</p>
              <p className="text-purple-700">A/B test different copy to see what resonates with your audience</p>
            </div>
          </div>
        </div>
      </div>
      <SaveBotton handleSave={handleSave} isSaving={isLoading} disabled={!hasChanges} />
    </div>
  )
}

export default Basic